import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, rupiahShort } from "@/lib/format";
import { toast } from "sonner";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import ReportOverview from "@/components/ReportOverview";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  CalendarIcon, FileSpreadsheet, FileText, TrendingUp, Coins, Receipt, Package, RefreshCw,
  ChevronDown, AlertTriangle, Boxes, TrendingDown, Search, ArrowLeftRight,
} from "lucide-react";

const fmt = (d) => format(d, "yyyy-MM-dd");
const label = (d) => format(d, "d MMM yyyy", { locale: idLocale });

export default function Reports() {
  const [range, setRange] = useState({ from: new Date(), to: new Date() });
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [compact, setCompact] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => { const media=window.matchMedia("(max-width: 767px)"), change=e=>setCompact(e.matches); media.addEventListener("change",change); return ()=>media.removeEventListener("change",change); }, []);
  const [calOpen, setCalOpen] = useState(false);
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState(null);
  const [closing, setClosing] = useState(null);
  const [tab, setTab] = useState("ringkasan");
  const [expTx, setExpTx] = useState(null);
  const [movements, setMovements] = useState(null);
  const [mvType, setMvType] = useState("all");
  const [mvQ, setMvQ] = useState("");

  const start = range?.from ? fmt(range.from) : fmt(new Date());
  const end = range?.to ? fmt(range.to) : start;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(""); setSales(null); setProducts(null); setClosing(null);
    const params = {start, end};
    Promise.all([
      api.get("/reports/sales", {params, signal:controller.signal}),
      api.get("/reports/products", {params, signal:controller.signal}),
      api.get("/reports/closing", {params, signal:controller.signal}),
    ]).then(([s,p,c]) => {setSales(s.data); setProducts(p.data); setClosing(c.data);})
      .catch(e => {if (!controller.signal.aborted) setError(apiError(e));})
      .finally(() => {if (!controller.signal.aborted) setLoading(false);});
    return () => controller.abort();
  }, [start, end, refresh]);

  useEffect(() => {
    const controller = new AbortController(); setMovements(null);
    api.get("/reports/stock-movements", {params:{start,end,type:mvType,q:mvQ},signal:controller.signal})
      .then(r => setMovements(r.data))
      .catch(e => {if (!controller.signal.aborted) toast.error(apiError(e));});
    return () => controller.abort();
  }, [start, end, mvType, mvQ, refresh]);

  const download = async (type, format2, extra = {}) => {
    try {
      const res = await api.get("/reports/export", { params: { type, format: format2, start, end, ...extra }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}.${format2 === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const preset = (days, mode) => {
    const now = new Date();
    if (mode === "today") setRange({ from: now, to: now });
    else if (mode === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); setRange({ from: y, to: y }); }
    else if (mode === "month") setRange({ from: new Date(now.getFullYear(), now.getMonth(), 1), to: now });
    else { const f = new Date(now); f.setDate(f.getDate() - (days - 1)); setRange({ from: f, to: now }); }
    setCalOpen(false);
  };

  const rangeText = range?.from
    ? (range.to && fmt(range.to) !== fmt(range.from) ? `${label(range.from)} — ${label(range.to)}` : label(range.from))
    : "Pilih tanggal";

  return (
    <>
      <PageHeader title="Laporan" subtitle="Ringkasan usaha, penjualan, persediaan & audit">
        <div className="ml-auto flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto">
        <Button variant="outline" aria-label="Perbarui laporan" title="Perbarui laporan" className="h-9 w-9 shrink-0 border-ui-line bg-ui-card p-0 sm:h-10 sm:w-auto sm:px-3" disabled={loading} onClick={() => setRefresh(v => v+1)}><RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? "animate-spin" : ""}`} /><span className="hidden sm:inline">Perbarui</span></Button>
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <Button data-testid="report-date-btn" variant="outline" className="h-9 max-w-[calc(100vw-5.5rem)] min-w-0 gap-1.5 border-ui-line bg-ui-card px-2 hover:bg-ui-card sm:h-10 sm:max-w-none sm:gap-2 sm:px-3">
              <CalendarIcon className="h-4 w-4 shrink-0 text-brand-readable" />
              <span className="max-w-[190px] truncate text-xs sm:text-sm">{rangeText}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={6} className="w-auto max-w-[calc(100vw-1rem)] p-0 bg-ui-card border-ui-line text-ui-text" data-testid="report-calendar">
            <div className="flex flex-wrap gap-1 p-2 sm:gap-1.5 sm:p-3 border-b border-ui-line">
              <Chip onClick={() => preset(0, "today")}>Hari ini</Chip>
              <Chip onClick={() => preset(0, "yesterday")}>Kemarin</Chip>
              <Chip onClick={() => preset(7)}>7 Hari</Chip>
              <Chip onClick={() => preset(30)}>30 Hari</Chip>
              <Chip onClick={() => preset(0, "month")}>Bulan Ini</Chip>
            </div>
            <Calendar
              mode="range"
              selected={range}
              onSelect={(r) => setRange(r || { from: new Date(), to: new Date() })}
              numberOfMonths={compact ? 1 : 2}
              locale={idLocale}
            />
          </PopoverContent>
        </Popover>
        </div>
      </PageHeader>

      <div className="flex-1 min-w-0 overflow-y-auto p-3 sm:p-5" data-testid="reports-page">
        {loading && <p role="status" className="theme-panel mb-4 text-sm text-ui-muted">Memuat laporan…</p>}
        {error && <div role="alert" className="theme-panel mb-4 text-danger-readable">{error}<button className="theme-outline ml-3" onClick={() => setRefresh(v => v+1)}>Coba lagi</button></div>}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full max-w-full overflow-x-auto bg-ui-card border border-ui-line h-auto flex flex-nowrap justify-start gap-1 p-1 sm:flex-wrap">
            <TabsTrigger value="ringkasan" data-testid="tab-ringkasan">Ringkasan</TabsTrigger>
            <TabsTrigger value="penjualan" data-testid="tab-penjualan">Penjualan</TabsTrigger>
            <TabsTrigger value="transaksi" data-testid="tab-transaksi">Per Transaksi</TabsTrigger>
            <TabsTrigger value="produk" data-testid="tab-produk">Produk & Stok</TabsTrigger>
            <TabsTrigger value="closing" data-testid="tab-closing">Closing Shift</TabsTrigger>
            <TabsTrigger value="mutasi" data-testid="tab-mutasi">Mutasi Stok</TabsTrigger>
          </TabsList>

          <TabsContent value="ringkasan" className="mt-5 space-y-4">
            {sales && products && <><ExportBar onExcel={() => download("sales", "xlsx")} onPdf={() => download("sales", "pdf")} /><ReportOverview sales={sales} products={products} rangeText={rangeText} onSelect={setTab}/></>}
          </TabsContent>

          {/* PENJUALAN */}
          <TabsContent value="penjualan" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("sales", "xlsx")} onPdf={() => download("sales", "pdf")} />
            {sales && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Pendapatan" value={rupiah(sales.summary.revenue)} icon={TrendingUp} color="text-brand-readable" bg="bg-brand-soft" testid="stat-revenue" />
                  <Stat label="Keuntungan" value={rupiah(sales.summary.profit)} icon={Coins} color="text-warning-readable" bg="bg-warning-soft" sub={`Margin ${sales.summary.margin.toFixed(1)}%`} testid="stat-profit" />
                  <Stat label="Modal (HPP)" value={rupiah(sales.summary.cogs)} icon={Package} color="text-accent-readable" bg="bg-accent-soft" />
                  <Stat label="Transaksi" value={`${sales.summary.tx_count} · ${sales.summary.items_sold} item`} icon={Receipt} color="text-accent-readable" bg="bg-accent-soft" />
                </div>

                <Panel title="Tren Pendapatan & Untung">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={sales.by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--masivi-border)" />
                      <XAxis dataKey="label" stroke="var(--masivi-card-muted)" fontSize={12} />
                      <YAxis stroke="var(--masivi-card-muted)" fontSize={12} tickFormatter={rupiahShort} width={60} />
                      <Tooltip contentStyle={{ background: "var(--masivi-card-bg)", color: "var(--masivi-card-text)", border: "1px solid var(--masivi-border)", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
                      <Line type="monotone" dataKey="revenue" name="Pendapatan" stroke="var(--masivi-primary)" strokeWidth={3} dot={false} />
                      <Line type="monotone" dataKey="profit" name="Untung" stroke="var(--masivi-warning)" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel title="Item Penjualan per Hari (dengan Keuntungan)">
                  <SimpleTable
                    testid="items-table"
                    head={["Produk", "Satuan", "Qty", "Pendapatan", "Modal", "Untung"]}
                    align={["l", "l", "r", "r", "r", "r"]}
                    rows={sales.items.map((i) => [i.name, i.unit, i.qty, rupiah(i.revenue), rupiah(i.cogs), <span className="text-warning-readable font-semibold">{rupiah(i.profit)}</span>])}
                    empty="Belum ada penjualan pada rentang ini"
                  />
                </Panel>
              </>
            )}
          </TabsContent>

          {/* PER TRANSAKSI */}
          <TabsContent value="transaksi" className="mt-5 space-y-3">
            {sales && (sales.transactions.length === 0 ? (
              <Empty text="Belum ada transaksi pada rentang ini" />
            ) : (
              sales.transactions.map((t) => {
                const open = expTx === t.id;
                return (
                  <div key={t.id} data-testid={`tx-${t.id}`} className="rounded-xl border border-ui-line bg-ui-soft overflow-hidden">
                    <button onClick={() => setExpTx(open ? null : t.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-ui-card">
                      <ChevronDown className={`h-4 w-4 text-ui-muted transition-transform ${open ? "rotate-180" : ""}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-semibold">{t.invoice_no}</div>
                        <div className="text-[11px] text-ui-muted">{new Date(t.created_at).toLocaleString("id-ID")} · {t.cashier} · {t.item_count} item</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-brand-readable">{rupiah(t.total)}</div>
                        <div className="text-[11px] text-warning-readable">Untung {rupiah(t.profit)}</div>
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-ui-line">
                        <SimpleTable
                          head={["Produk", "Satuan", "Qty", "Harga", "Modal", "Untung"]}
                          align={["l", "l", "r", "r", "r", "r"]}
                          rows={t.items.map((i) => [i.name, i.unit, i.qty, rupiah(i.price), rupiah(i.cost), <span className="text-warning-readable font-semibold">{rupiah(i.profit)}</span>])}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ))}
          </TabsContent>

          {/* PRODUK & STOK */}
          <TabsContent value="produk" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("products", "xlsx")} onPdf={() => download("products", "pdf")} />
            {products && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Stat label="Total Produk" value={products.totals.product_count} icon={Package} color="text-accent-readable" bg="bg-accent-soft" />
                  <Stat label="Total Sisa Stok (pcs)" value={products.totals.total_stock} icon={Boxes} color="text-brand-readable" bg="bg-brand-soft" />
                  <Stat label="Nilai Stok (modal)" value={rupiah(products.totals.stock_value)} icon={Coins} color="text-warning-readable" bg="bg-warning-soft" />
                </div>

                {(() => {
                  const minus = products.products.filter((p) => p.stock < 0);
                  return minus.length > 0 ? (
                    <Panel title="⚠ Stok Minus — Terjual Melebihi Stok (perlu segera dibeli)">
                      <SimpleTable
                        testid="stock-minus-table"
                        head={["Produk", "Kategori", "Stok (minus)", "Terjual"]}
                        align={["l", "l", "r", "r"]}
                        rows={minus.map((p) => [
                          p.name, p.category,
                          <span className="text-danger-readable font-bold inline-flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" />{p.stock}</span>,
                          p.sold_qty,
                        ])}
                      />
                    </Panel>
                  ) : null;
                })()}
                <Panel title="Sisa Stok per Kategori">
                  <SimpleTable
                    testid="stock-category-table"
                    head={["Kategori", "Jml Produk", "Total Stok", "Nilai Stok"]}
                    align={["l", "r", "r", "r"]}
                    rows={products.by_category.map((c) => [c.category, c.product_count, c.total_stock, rupiah(c.stock_value)])}
                    empty="Belum ada produk"
                  />
                </Panel>

                <Panel title="Laporan Produk & Sisa Stok">
                  <SimpleTable
                    testid="product-report-table"
                    head={["Produk", "Kategori", "Sisa Stok", "Min", "Modal", "Harga", "Nilai", "Terjual"]}
                    align={["l", "l", "r", "r", "r", "r", "r", "r"]}
                    rows={products.products.map((p) => [
                      p.name, p.category,
                      <span className={p.low ? "text-warning-readable font-semibold inline-flex items-center gap-1" : ""}>{p.low && <AlertTriangle className="h-3 w-3" />}{p.stock}</span>,
                      p.min_stock, rupiah(p.cost), rupiah(p.price), rupiah(p.stock_value), p.sold_qty,
                    ])}
                    empty="Belum ada produk"
                  />
                </Panel>
              </>
            )}
          </TabsContent>

          {/* CLOSING SHIFT */}
          <TabsContent value="closing" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("closing", "xlsx")} onPdf={() => download("closing", "pdf")} />
            {closing && (
              <Panel title={`Closing Shift per Kasir · ${start === end ? label(range.from) : rangeText}`}>
                <SimpleTable
                  testid="closing-table"
                  head={["Kasir", "Transaksi", "Item", "Penjualan", "Untung", "Tunai", "Transfer", "QRIS", "Kembalian", "Piutang", "Pelunasan"]}
                  align={["l", "r", "r", "r", "r", "r", "r", "r", "r", "r", "r"]}
                  rows={closing.rows.map((r) => [
                    r.cashier, r.tx_count, r.items_sold, rupiah(r.revenue),
                    <span className="text-warning-readable font-semibold">{rupiah(r.profit)}</span>,
                    rupiah(r.tunai), rupiah(r.transfer), rupiah(r.qris), rupiah(r.change),
                    <span className="text-danger-readable">{rupiah(r.piutang || 0)}</span>,
                    <span className="text-brand-readable font-semibold">{rupiah(r.pelunasan || 0)}</span>,
                  ])}
                  footer={closing.rows.length ? [
                    "TOTAL", closing.totals.tx_count, closing.totals.items_sold, rupiah(closing.totals.revenue),
                    rupiah(closing.totals.profit), rupiah(closing.totals.tunai), rupiah(closing.totals.transfer),
                    rupiah(closing.totals.qris), rupiah(closing.totals.change),
                    rupiah(closing.totals.piutang || 0), rupiah(closing.totals.pelunasan || 0),
                  ] : null}
                  empty="Belum ada transaksi pada rentang ini"
                />
              </Panel>
            )}
          </TabsContent>

          {/* MUTASI STOK */}
          <TabsContent value="mutasi" className="mt-5 space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={mvType} onValueChange={setMvType}>
                <SelectTrigger data-testid="mutasi-type-filter" className="w-[160px] h-9 border-ui-line bg-ui-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-ui-card border-ui-line text-ui-text">
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="Pembelian">Pembelian</SelectItem>
                  <SelectItem value="Penjualan">Penjualan</SelectItem>
                  <SelectItem value="Opname">Opname</SelectItem>
                  <SelectItem value="Transfer">Transfer</SelectItem>
                  <SelectItem value="Refund">Refund</SelectItem>
                  <SelectItem value="Penyesuaian">Penyesuaian</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ui-muted" />
                <Input
                  data-testid="mutasi-search"
                  value={mvQ}
                  onChange={(e) => setMvQ(e.target.value)}
                  placeholder="Cari produk…"
                  className="pl-9 h-9 border-ui-line bg-ui-card"
                />
              </div>
              <div className="ml-auto">
                <ExportBar
                  onExcel={() => download("stock-movements", "xlsx", { mv_type: mvType, q: mvQ })}
                  onPdf={() => download("stock-movements", "pdf", { mv_type: mvType, q: mvQ })}
                />
              </div>
            </div>

            {movements && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Total Masuk (+)" value={`+${movements.totals.in}`} icon={TrendingUp} color="text-brand-readable" bg="bg-brand-soft" testid="stat-mv-in" />
                  <Stat label="Total Keluar (−)" value={`−${movements.totals.out}`} icon={TrendingDown} color="text-danger-readable" bg="bg-danger-soft" testid="stat-mv-out" />
                  <Stat label="Mutasi Bersih" value={`${movements.totals.net >= 0 ? "+" : ""}${movements.totals.net}`} icon={ArrowLeftRight} color="text-warning-readable" bg="bg-warning-soft" testid="stat-mv-net" />
                  <Stat label="Jumlah Mutasi" value={movements.totals.count} icon={Boxes} color="text-accent-readable" bg="bg-accent-soft" testid="stat-mv-count" />
                </div>

                <Panel title="Ringkasan per Jenis Mutasi">
                  <SimpleTable
                    testid="mutasi-summary-table"
                    head={["Jenis", "Masuk (+)", "Keluar (−)", "Bersih", "Jml"]}
                    align={["l", "r", "r", "r", "r"]}
                    rows={movements.summary.map((g) => [
                      g.type,
                      <span className="text-brand-readable font-semibold">+{g.in}</span>,
                      <span className="text-danger-readable font-semibold">−{g.out}</span>,
                      <span className={g.net >= 0 ? "text-brand-readable" : "text-danger-readable"}>{g.net >= 0 ? "+" : ""}{g.net}</span>,
                      g.count,
                    ])}
                    footer={movements.summary.length ? [
                      "TOTAL",
                      `+${movements.totals.in}`,
                      `−${movements.totals.out}`,
                      `${movements.totals.net >= 0 ? "+" : ""}${movements.totals.net}`,
                      movements.totals.count,
                    ] : null}
                    empty="Belum ada mutasi pada rentang ini"
                  />
                </Panel>

                <Panel title={`Detail Mutasi Stok · ${start === end ? label(range.from) : rangeText}`}>
                  <SimpleTable
                    testid="mutasi-detail-table"
                    head={["Waktu", "Jenis", "Produk", "Perubahan", "Catatan", "Oleh"]}
                    align={["l", "l", "l", "r", "l", "l"]}
                    rows={movements.movements.map((m) => [
                      m.at_label,
                      <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-ui-muted-surface/60 text-ui-text">{m.type}</span>,
                      m.product_name,
                      <span className={m.delta >= 0 ? "text-brand-readable font-bold" : "text-danger-readable font-bold"}>{m.delta >= 0 ? `+${m.delta}` : m.delta}</span>,
                      <span className="text-ui-muted text-xs">{m.note || "-"}</span>,
                      <span className="text-ui-muted text-xs">{m.by || "-"}</span>,
                    ])}
                    empty="Belum ada mutasi pada rentang ini"
                  />
                </Panel>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function Chip({ children, onClick }) {
  return (
    <button onClick={onClick} className="px-2.5 h-8 rounded-lg text-xs font-medium bg-ui-card border border-ui-line hover:border-brand text-ui-text no-select">
      {children}
    </button>
  );
}

function ExportBar({ onExcel, onPdf }) {
  return (
    <div className="flex justify-end gap-2">
      <Button data-testid="export-excel-btn" onClick={onExcel} variant="outline" className="border-brand/50 bg-brand-soft hover:bg-brand-soft text-brand-readable gap-2 h-9">
        <FileSpreadsheet className="h-4 w-4" /> Excel
      </Button>
      <Button data-testid="export-pdf-btn" onClick={onPdf} variant="outline" className="border-danger/50 bg-danger-soft hover:bg-danger-soft text-danger-readable gap-2 h-9">
        <FileText className="h-4 w-4" /> PDF
      </Button>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, bg, sub, testid }) {
  return (
    <div data-testid={testid} className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 sm:p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-ui-muted uppercase tracking-wide">{label}</span>
        <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`h-4 w-4 ${color}`} /></div>
      </div>
      <div className="break-words font-mono font-extrabold text-lg sm:text-xl mt-2">{value}</div>
      {sub && <div className="text-[11px] text-ui-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="min-w-0 rounded-xl border border-ui-line bg-ui-soft p-3 sm:p-4">
      <h3 className="break-words font-heading font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-ui-line bg-ui-soft p-10 text-center text-ui-muted text-sm">{text}</div>;
}

function SimpleTable({ head, rows, align = [], footer, empty, testid }) {
  const cls = (i) => (align[i] === "r" ? "text-right" : "text-left");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="text-ui-muted text-[11px] uppercase tracking-wide border-b border-ui-line">
          <tr>{head.map((h, i) => <th key={i} className={`py-2 px-2 ${cls(i)}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="py-8 text-center text-ui-muted">{empty}</td></tr>
          ) : rows.map((r, ri) => (
            <tr key={ri} className="border-b border-ui-line hover:bg-ui-soft">
              {r.map((c, ci) => <td key={ci} className={`py-2 px-2 ${cls(ci)} ${ci === 0 ? "font-medium" : "font-mono"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t-2 border-ui-line font-bold bg-ui-soft">
              {footer.map((c, ci) => <td key={ci} className={`py-2 px-2 ${cls(ci)} ${ci === 0 ? "" : "font-mono"}`}>{c}</td>)}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
